import {parseTimeZone, parseCalendarDate, parseInstant} from '@liquid-spirit/date-time/validation';
import {calendarDateAtInstant} from '@liquid-spirit/date-time/display';

/** Explicit zones opt into canonical reads; omitted queries retain their old adapters. */
export function readOccurrenceDisplayTimeZone(query) {
  return Object.hasOwn(query, 'displayTimeZone') ? parseTimeZone(query.displayTimeZone) : undefined;
}

export function isOccurrenceReadContext(value) {
  try {
    if (!value || typeof value !== 'object' || Array.isArray(value) || value.version !== 1) return false;
    const zone = parseTimeZone(value.displayTimeZone);
    const now = parseInstant(value.asOf);
    return parseCalendarDate(value.today) === calendarDateAtInstant(now, zone);
  } catch { return false; }
}

/** Preserve optional metadata for clients that need server-owned calendar freshness. */
export function readOccurrenceReadContext(payload) {
  if (!Object.hasOwn(payload, 'occurrenceContext')) return undefined;
  if (!isOccurrenceReadContext(payload.occurrenceContext)) {
    throw Object.assign(new Error('Invalid occurrence read context.'), {code: 'INVALID_API_RESPONSE'});
  }
  return payload.occurrenceContext;
}

/** Client invalidation scope. The server still owns the read instant. */
export function occurrenceCalendarRequestScope({displayTimeZone, now = Date.now()} = {}) {
  const zone = parseTimeZone(displayTimeZone);
  const today = calendarDateAtInstant(new Date(now).toISOString(), zone);
  return {query: {displayTimeZone: zone}, today, key: JSON.stringify([zone, today])};
}

/** A projection may only hydrate the calendar that requested it. Old servers remain compatible. */
export function assertOccurrenceResponseScope(payload, scope) {
  const context = readOccurrenceReadContext(payload);
  if (context && (context.displayTimeZone !== scope.query.displayTimeZone || context.today !== scope.today)) {
    throw Object.assign(new Error('Occurrence response belongs to another calendar context.'), {code: 'INVALID_API_RESPONSE'});
  }
  return payload;
}
